/**
 * FittingIcon — tiny 32×24 SVG glyph that depicts a tray-fitting's geometry.
 * Drawn purely from `fitting_type` so we don't need per-SKU artwork.
 *
 * Used in the routing-suggestion card picker (QuickJoinDialog + JoinTraysPanel)
 * so the user can scan options visually before clicking.
 */
const STROKE = "#00E5FF";
const ACCENT = "#FFCC00";

function ElbowH({ deg }) {
  // Horizontal elbow: a leg coming in along +X meeting a leg leaving upward.
  // deg=90 → right angle, deg=45 → diagonal.
  const x1 = 2,  y1 = 18;
  const x2 = 16, y2 = 18;          // corner (always the bend point)
  const x3 = deg === 45 ? 28 : 16;
  const y3 = deg === 45 ? 6  : 6;
  return (
    <>
      <polyline points={`${x1},${y1} ${x2},${y2} ${x3},${y3}`}
                fill="none" stroke={STROKE} strokeWidth="2" />
      <circle cx={x2} cy={y2} r="2" fill={ACCENT} />
    </>
  );
}

function ElbowV({ deg }) {
  // Vertical elbow — same idea, but rendered with one leg dropping vertically
  // and the second leg leaving horizontally (the bend axis is "into the page").
  const x1 = 8,  y1 = 4;
  const x2 = 8,  y2 = 16;          // corner
  const x3 = deg === 45 ? 28 : 22;
  const y3 = deg === 45 ? 4  : 16;
  return (
    <>
      <polyline points={`${x1},${y1} ${x2},${y2} ${x3},${y3}`}
                fill="none" stroke={STROKE} strokeWidth="2" />
      <circle cx={x2} cy={y2} r="2" fill={ACCENT} />
    </>
  );
}

function Tee() {
  return (
    <>
      <line x1="2"  y1="18" x2="30" y2="18" stroke={STROKE} strokeWidth="2" />
      <line x1="16" y1="18" x2="16" y2="4"  stroke={STROKE} strokeWidth="2" />
      <circle cx="16" cy="18" r="2" fill={ACCENT} />
    </>
  );
}

function Cross() {
  return (
    <>
      <line x1="2"  y1="12" x2="30" y2="12" stroke={STROKE} strokeWidth="2" />
      <line x1="16" y1="2"  x2="16" y2="22" stroke={STROKE} strokeWidth="2" />
      <circle cx="16" cy="12" r="2" fill={ACCENT} />
    </>
  );
}

function Reducer() {
  // Tapered horizontal pipe — wide left, narrow right.
  return (
    <>
      <polygon points="2,8 14,8 28,13 28,17 14,18 2,18"
                fill="none" stroke={STROKE} strokeWidth="2" strokeLinejoin="round" />
    </>
  );
}

function Dropout() {
  // Run that drops downward (cable falling out).
  return (
    <>
      <line x1="2"  y1="10" x2="22" y2="10" stroke={STROKE} strokeWidth="2" />
      <line x1="22" y1="10" x2="22" y2="22" stroke={STROKE} strokeWidth="2" strokeDasharray="2 2" />
      <circle cx="22" cy="10" r="2" fill={ACCENT} />
    </>
  );
}

const SHAPES = {
  horizontal_elbow_90: <ElbowH deg={90} />,
  horizontal_elbow_45: <ElbowH deg={45} />,
  vertical_elbow_90:   <ElbowV deg={90} />,
  vertical_elbow_45:   <ElbowV deg={45} />,
  horizontal_tee:      <Tee />,
  horizontal_cross:    <Cross />,
  reducer:             <Reducer />,
  dropout:             <Dropout />,
};

export default function FittingIcon({ kind, size = 24 }) {
  const shape = SHAPES[kind] || (
    <line x1="2" y1="12" x2="30" y2="12" stroke={STROKE} strokeWidth="2" />
  );
  return (
    <svg width={size * 1.333} height={size} viewBox="0 0 32 24"
         aria-hidden="true" data-testid={`fitting-icon-${kind}`}>
      {shape}
    </svg>
  );
}
